import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fetchPrivateNetwork, setNetworkStatus} from '../store/slices/privateNetworkSlice';
import type {RootState, AppDispatch} from '../store/store';
import {FaCopy, FaPlus, FaSyncAlt, FaTrash} from "react-icons/fa";
import InfoBanner from '../components/InfoBanner';
import {useToast} from "../components/ToastContext";
import {useConfirm} from "../components/ConfirmContext";
import axiosInstance from "../utility/axiosInstance";
import {handleAxiosError} from "../helpers/handleAxiosError";
import Modal from "../components/Modal";
import Select from 'react-select';
import TrialLock from '../components/TrialLock';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';

const ITEMS_PER_PAGE = 10;

const shortNetworkId = (networkId: string): string =>
    networkId ? `${networkId.slice(0, 8)}...` : 'Not assigned';

const PrivateNetworksPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const privateNetwork = useSelector((state: RootState) => state.privateNetwork);

    const {showToast} = useToast();
    const {confirm} = useConfirm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newNetworkCIDR, setNewNetworkCIDR] = useState<string>('');
    const [showCreateNetwork, setShowCreateNetwork] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const creationLocked = useAutoOpenCreateModal(setShowCreateNetwork);

    useEffect(() => {
        if (privateNetwork.status === 'idle') {
            dispatch(fetchPrivateNetwork());
        }
    }, [dispatch, privateNetwork.status]);

    useEffect(() => {
        setCurrentPage(0);
    }, [privateNetwork.networks.length]);

    const totalNetworks = privateNetwork.networks.length;
    const totalPages = Math.ceil(totalNetworks / ITEMS_PER_PAGE);
    const paginatedNetworks = privateNetwork.networks.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const handleCreateNetwork = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        let cidr = newNetworkCIDR;
        if (cidr && !cidr.includes('/')) {
            cidr = cidr + '/24';
        }

        const cidrRegex = /^10\.1\.\d{1,3}\.0\/24$/;
        if (cidr && !cidrRegex.test(cidr)) {
            showToast('Invalid CIDR. Use format: 10.1.x.0/24 or 10.1.x.0', 'error');
            setLoading(false);
            return;
        }

        try {
            const requestBody = {
                requestedCIDR: cidr || undefined,
            };

            const response = await axiosInstance.post('/v1/private_network', requestBody);

            if (response.status === 200) {
                showToast('Network created successfully!', 'info');
                dispatch(fetchPrivateNetwork());
                setNewNetworkCIDR('');
                setShowCreateNetwork(false);
            } else {
                throw new Error('Failed to create network');
            }
        } catch (err) {
            const errorMessage = handleAxiosError(err);
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNetwork = async (networkId: string, networkName: string) => {
        if (networkName?.toLowerCase().includes('default')) {
            showToast('Default networks cannot be deleted.', 'error');
            return;
        }

        const proceed = await confirm({
            title: 'Delete Private Network',
            message: `Are you sure you want to delete network "${networkName || networkId}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        dispatch(setNetworkStatus({id: networkId, status: 'deleting'}));

        try {
            await axiosInstance.delete(`/v1/private_network/${networkId}`);
            showToast(`Network "${networkName || networkId}" deleted`, 'success');
            dispatch(fetchPrivateNetwork());
        } catch (err) {
            console.error('Delete network error:', err);
            const errorMessage = handleAxiosError(err);
            showToast(errorMessage, 'error');
            dispatch(setNetworkStatus({id: networkId, status: 'active'}));
        }
    };

    const handleCopyNetworkId = async (networkId: string) => {
        try {
            await navigator.clipboard.writeText(networkId);
            showToast('Network ID copied', 'success');
        } catch {
            showToast('Could not copy the network ID. Select it and copy it manually.', 'error');
        }
    };

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Create isolated virtual networks for your cloud resources
                </p>
                <a href="https://docs.redu.cloud/Guides/networking-guide" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
                <InfoBanner id="private-networks-info">
                    <span>Networks used by instances or default networks cannot be deleted.</span>
                </InfoBanner>
            </div>

            {loading && (
                <div className="loading-overlay">
                    <FaSyncAlt className="spin"/>
                </div>
            )}

            {error && <div className="error-banner"><p>{error}</p></div>}

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <caption className="visually-hidden">Private networks</caption>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>CIDR</th>
                            <th>Status</th>
                            <th>Network ID</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {privateNetwork.status === 'loading' ? (
                            <tr>
                                <td colSpan={5} className="loading-state">
                                    <FaSyncAlt className="spin"/> Loading private networks...
                                </td>
                            </tr>
                        ) : privateNetwork.status === 'failed' ? (
                            <tr>
                                <td colSpan={5} className="error-state" role="alert">
                                    <p>Error: {privateNetwork.error}</p>
                                </td>
                            </tr>
                        ) : paginatedNetworks.length > 0 ? (
                            paginatedNetworks.map((network) => (
                                <tr key={network.id}>
                                    <td className="keypair-name">{network.name || 'Unnamed network'}</td>
                                    <td>{network.cidr || '-'}</td>
                                    <td>
                                        {network.status === 'deleting' ? (
                                            <div className="status-label" style={{color: '#ef4444'}}>DELETING</div>
                                        ) : (
                                            <span className="text-success">Active</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="technical-id" title={network.id}>
                                            <code>{shortNetworkId(network.id)}</code>
                                            <button type="button" onClick={() => handleCopyNetworkId(network.id)} aria-label={`Copy network ID for ${network.name || 'private network'}`} title="Copy network ID">
                                                <FaCopy aria-hidden="true" />
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        <Select
                                            options={[
                                                {
                                                    value: 'delete',
                                                    label: 'Delete Network',
                                                    isDisabled: network.name?.toLowerCase().includes('default')
                                                }
                                            ]}
                                            onChange={(option) => {
                                                if (option?.value === 'delete' && !option.isDisabled) {
                                                    handleDeleteNetwork(network.id, network.name);
                                                }
                                            }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            aria-label={`Actions for ${network.name || 'private network'}`}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    <div className="resource-empty-state">
                                        <strong>No private networks yet</strong>
                                        <span>Create one to isolate traffic between your resources.</span>
                                        <TrialLock><button type="button" onClick={() => setShowCreateNetwork(true)}>Create private network</button></TrialLock>
                                    </div>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalNetworks)} of {totalNetworks}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mobile-view">
                {privateNetwork.status === 'loading' ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin"/>
                        <p>Loading private networks...</p>
                    </div>
                ) : privateNetwork.status === 'failed' ? (
                    <div className="error-state">
                        <p>Error: {privateNetwork.error}</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedNetworks.length > 0 ? (
                            paginatedNetworks.map((network) => (
                                <div key={network.id} className="keypair-card">
                                    <div className="card-header">
                                        <h3 className="card-title" title={network.id}>{network.name || `Network ${shortNetworkId(network.id)}`}</h3>
                                        <button
                                            className="delete-icon-button"
                                            onClick={() => !network.name?.toLowerCase().includes('default') && handleDeleteNetwork(network.id, network.name)}
                                            disabled={network.name?.toLowerCase().includes('default')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: network.name?.toLowerCase().includes('default') ? '#ccc' : '#ef4444',
                                                cursor: network.name?.toLowerCase().includes('default') ? 'not-allowed' : 'pointer'
                                            }}
                                            title={network.name?.toLowerCase().includes('default') ? "Default networks cannot be deleted" : "Delete Network"}
                                            aria-label={network.name?.toLowerCase().includes('default') ? "Default networks cannot be deleted" : `Delete ${network.name || 'private network'}`}
                                        >
                                            <FaTrash/>
                                        </button>
                                    </div>
                                    <div className="card-content">
                                        <div className="fingerprint">
                                            <span className="label">Name:</span>
                                            <span className="value">{network.name || '-'}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">CIDR:</span>
                                            <span className="value">{network.cidr || '-'}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Status:</span>
                                            <span className="value"
                                                  style={{color: network.status === 'deleting' ? '#ef4444' : 'forestgreen'}}>
                                                {network.status === 'deleting' ? 'DELETING' : 'Active'}
                                            </span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Network ID:</span>
                                            <span className="value technical-id" title={network.id}>
                                                <code>{shortNetworkId(network.id)}</code>
                                                <button type="button" onClick={() => handleCopyNetworkId(network.id)} aria-label={`Copy network ID for ${network.name || 'private network'}`} title="Copy network ID">
                                                    <FaCopy aria-hidden="true" />
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="resource-empty-state">
                                    <strong>No private networks yet</strong>
                                    <span>Create one to isolate traffic between your resources.</span>
                                    <TrialLock><button type="button" onClick={() => setShowCreateNetwork(true)}>Create private network</button></TrialLock>
                                </div>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalNetworks)} of {totalNetworks}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>


            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setShowCreateNetwork(true)}
                    title="Create New Network"
                >
                    <FaPlus/>
                    <span>Create Network</span>
                </button>
            </TrialLock>


            <Modal
                isOpen={showCreateNetwork && !creationLocked}
                onClose={() => {
                    setShowCreateNetwork(false);
                    setNewNetworkCIDR('');
                }}
                title="Create Private Network"
            >
                <form onSubmit={handleCreateNetwork} className="create-form">
                    <div className="form-group">
                        <label htmlFor="new-network-cidr">CIDR (optional)</label>
                        <input
                            id="new-network-cidr"
                            type="text"
                            value={newNetworkCIDR}
                            onChange={(e) => setNewNetworkCIDR(e.target.value)}
                            placeholder="e.g., 10.1.5.0/24 or leave empty"
                            autoFocus
                        />
                        <p className="help-text small">
                            Leave blank to auto-assign the next available CIDR block.
                        </p>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setShowCreateNetwork(false);
                                setNewNetworkCIDR('');
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button disabled={loading} className="submit-button" type="submit">
                            {loading ? 'Creating...' : 'Create Network'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default PrivateNetworksPage;
